import React, { useState, useEffect } from "react";
import { Settings, LogOut, X, Check, Cpu, Loader2 } from "lucide-react";
import { kreieren } from "@/api/kreierenClient";
import { morpheus } from "@/api/morpheusClient";
import ModellWahl from "@/components/ModellWahl";
import { getUser, logout } from "@/api/auth";
import { THEMES, getTheme, setTheme } from "@/theme";

export default function SettingsLauncher() {
  const [open, setOpen] = useState(false);
  const [theme, setT] = useState(getTheme());
  const user = getUser() || {};
  const acct = user.email || user.name || user.username || "Angemeldet";
  const apply = (k) => { setTheme(k); setT(k); };

  // ── Modell-Umschalter: Zustand kommt vom Server (.env + llm_modelle.json),
  // nicht aus dem Frontend. Der Wechsel startet nichts neu — er schreibt die .env,
  // und beide Dienste ziehen beim nächsten Lauf nach. Während eines laufenden
  // Schnitts ist er gesperrt, damit klar bleibt, welches Modell ein Reel gemacht hat.
  const [llm, setLlm] = useState(null);
  const [llmFehler, setLlmFehler] = useState("");
  const [wechselt, setWechselt] = useState("");
  const llmLaden = () => kreieren.getLlm().then(setLlm).catch((e) => setLlmFehler(e.message));
  useEffect(() => { if (open) { setLlmFehler(""); llmLaden(); } }, [open]);

  // Kein Warten auf einen Neustart mehr: seit 25.08. liest der Agent die .env,
  // sobald sie sich geändert hat, und zieht beim nächsten Lauf nach.
  const modellWaehlen = async (key) => {
    const m = (llm?.modelle || []).find((x) => x.key === key);
    if (wechselt || key === llm?.aktiv || !m) return;
    if (llm?.laufende_runs?.length) return;           // während eines Schnitts gar nicht erst
    if (!window.confirm(`Modell auf „${m.label}" (${m.anbieter}) umstellen?\n\nGilt ab dem nächsten Lauf. Laufende Schnitte bleiben auf dem alten Modell.`)) return;
    setLlmFehler(""); setWechselt(key);
    try {
      const r = await kreieren.setLlm(key);
      if (!r?.success) setLlmFehler(r?.error || "Umschalten fehlgeschlagen");
      await llmLaden();
    } catch (e) { setLlmFehler(e.message); }
    setWechselt("");
  };
  return (
    <>
      <button onClick={() => setOpen(true)} title="Account & Einstellungen"
        className="fixed bottom-4 left-3 z-50 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-[#13131D]/90 border border-white/10 backdrop-blur hover:border-[var(--signal)]/40 text-gray-300 hover:text-white transition shadow-lg">
        <span className="w-6 h-6 rounded-full bg-[rgba(var(--signal-rgb),0.15)] text-[var(--signal)] grid place-items-center text-xs font-display">{(acct[0] || "?").toUpperCase()}</span>
        <Settings className="w-4 h-4" />
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#13131D] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg text-white">Einstellungen</h2>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="eyebrow mb-1">Account</div>
            <div className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-5">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-8 h-8 rounded-full bg-[rgba(var(--signal-rgb),0.15)] text-[var(--signal)] grid place-items-center text-sm font-display shrink-0">{(acct[0] || "?").toUpperCase()}</span>
                <div className="min-w-0"><div className="text-white text-sm truncate">{acct}</div><div className="text-ether text-[11px]">angemeldet</div></div>
              </div>
              <button onClick={logout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 text-gray-300 hover:text-[var(--flare)] hover:bg-white/10 transition shrink-0"><LogOut className="w-3.5 h-3.5" /> Abmelden</button>
            </div>

            <div className="eyebrow mb-2">Modell · Cutter</div>
            <div className="grid grid-cols-2 gap-2 mb-1">
              {(llm?.modelle || []).map((m) => {
                const aktiv = m.key === llm?.aktiv;
                const laeuft = !!llm?.laufende_runs?.length;
                const laedt = wechselt === m.key;
                return (
                  <button key={m.key} onClick={() => modellWaehlen(m.key)}
                    disabled={!m.bereit || !!wechselt || laeuft}
                    title={!m.bereit ? "Kein API-Key hinterlegt" : laeuft ? "Es läuft gerade ein Schnitt" : `${m.anbieter} · ${m.preis}`}
                    className={`flex items-center gap-2.5 rounded-xl border p-2.5 transition text-left ${aktiv ? "border-[var(--signal)]/60 bg-white/[0.05]" : "border-white/10 hover:border-white/25"} ${!m.bereit || wechselt || laeuft ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <span className="w-8 h-8 rounded-lg shrink-0 grid place-items-center border border-white/10 bg-white/[0.04] text-[var(--signal)]">
                      {laedt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-white truncate">{m.label}</span>
                      <span className="block text-ether text-[10px] truncate">{m.anbieter}{m.notiz ? ` · ${m.notiz}` : ""}</span>
                      <span className="block text-ether text-[10px] truncate opacity-70">{m.preis}</span>
                    </span>
                    {aktiv && !laedt && <Check className="w-4 h-4 text-[var(--signal)] shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] mb-5">
              {llmFehler ? <span className="text-[var(--flare)]">{llmFehler}</span>
                : wechselt ? <span className="text-ether">wird umgestellt …</span>
                : llm?.laufende_runs?.length ? (
                    <span className="text-[var(--flare)]">
                      ⚠️ Es läuft gerade ein Schnitt — Wechsel gesperrt, bis er durch ist.
                    </span>
                  )
                : llm ? (
                    <span className="text-ether">
                      Gilt ab dem nächsten Lauf, ohne Neustart. Laufende Schnitte bleiben auf dem
                      alten Modell. Endpoint: {llm.endpoint}
                    </span>
                  )
                : <span className="text-ether">lädt …</span>}
            </div>

            <ModellWahl
              titel="Modell · Morpheus"
              laden={() => morpheus.getLlm()}
              setzen={(k, agent) => morpheus.setLlm(k, agent)}
              hinweis="Gilt ab dem nächsten Turn, ohne Neustart."
            />

            <div className="eyebrow mb-2">Theme</div>
            <div className="grid grid-cols-2 gap-2">
              {THEMES.map((t) => (
                <button key={t.key} onClick={() => apply(t.key)}
                  className={`flex items-center gap-2.5 rounded-xl border p-2.5 transition ${theme === t.key ? "border-[var(--signal)]/60 bg-white/[0.05]" : "border-white/10 hover:border-white/25"}`}>
                  <span className="w-8 h-8 rounded-lg shrink-0 relative overflow-hidden border border-white/10" style={{ background: t.bg }}>
                    <span className="absolute bottom-1 right-1 w-3 h-3 rounded-full" style={{ background: t.accent }} />
                  </span>
                  <span className="flex-1 text-left min-w-0">
                    <span className="block text-sm text-white truncate" style={{ fontFamily: t.font }}>{t.label}</span>
                    <span className="block text-ether text-[10px] truncate">{t.fontName}</span>
                  </span>
                  {theme === t.key && <Check className="w-4 h-4 text-[var(--signal)]" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
